import { useState } from "react";
import { X, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
}

export default function DeliveryForm({ onClose, onSubmit }) {
  const [cliente, setCliente] = useState("");
  const [herramienta, setHerramienta] = useState("");
  const [fecha, setFecha] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cliente.trim() || !herramienta.trim() || !fecha) {
      toast.error("Completa todos los campos");
      return;
    }
    setSubmitting(true);
    try {
      // Use YYYY-MM-DD using local timezone components (avoid UTC off-by-one)
      const yyyy = fecha.getFullYear();
      const mm = String(fecha.getMonth() + 1).padStart(2, "0");
      const dd = String(fecha.getDate()).padStart(2, "0");
      await onSubmit({
        cliente: cliente.trim(),
        herramienta: herramienta.trim(),
        fecha_entrega: `${yyyy}-${mm}-${dd}`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4" data-testid="delivery-form-modal">
      <div className="bg-white border border-zinc-200 w-full max-w-lg my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div>
            <p className="eyebrow text-zinc-500">Nueva entrega</p>
            <h2 className="font-display text-xl font-bold mt-1">Captura los datos</h2>
          </div>
          <button onClick={onClose} data-testid="btn-close-form" className="p-2 hover:bg-zinc-100 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Cliente</label>
            <input
              type="text"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Ej. Constructora Norte S.A."
              data-testid="input-cliente"
              className="w-full px-4 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-zinc-950 transition-colors"
            />
          </div>

          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Herramienta</label>
            <input
              type="text"
              value={herramienta}
              onChange={(e) => setHerramienta(e.target.value)}
              placeholder="Ej. Taladro percutor Bosch GBH 2-26"
              data-testid="input-herramienta"
              className="w-full px-4 py-3 border border-zinc-300 bg-white text-sm focus:outline-none focus:border-zinc-950 transition-colors"
            />
          </div>

          <div>
            <label className="eyebrow text-zinc-600 mb-2 block">Fecha a entregar</label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="btn-date-picker"
                  className="w-full px-4 py-3 border border-zinc-300 bg-white text-sm text-left flex items-center justify-between hover:border-zinc-950 transition-colors"
                >
                  <span className={fecha ? "" : "text-zinc-400"}>
                    {fecha ? formatDate(fecha) : "Selecciona una fecha"}
                  </span>
                  <CalendarIcon className="h-4 w-4 text-zinc-500" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 border border-zinc-200" align="start">
                <Calendar
                  mode="single"
                  selected={fecha}
                  onSelect={setFecha}
                  initialFocus
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="btn-cancel-form"
              className="btn-tactile flex-1 py-3 border border-zinc-300 text-sm font-medium hover:bg-zinc-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="btn-submit-delivery"
              className="btn-tactile flex-[2] py-3 bg-ekin-600 text-white text-sm font-medium hover:bg-ekin-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? "Creando..." : "Crear entrega"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
